import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getSetting, setSetting } from '../../lib/settings'
import { SHOW_HUNTS_KEY, type Product } from '../../lib/product'

export interface ShellData {
  /** BookMe only: the Hunts screens are switched on (setting, or the user already has contacts). */
  showHunts: boolean
  /** The user has at least one outreach program (shows Inbox > Outreach). */
  hasOutreach: boolean
  /** The BookMe show-Hunts lookup has finished (always true in Hunts). */
  ready: boolean
  setShowHunts(next: boolean): void
}

/**
 * Cheap per-session lookups that decide what the shell shows. Every query is
 * a head-only count, and a failure keeps the default (show less in BookMe,
 * show Outreach in Hunts so nothing a user relies on disappears).
 */
export function useShellData(userId: string | null, product: Product): ShellData {
  const [showHunts, setShowHuntsState] = useState<boolean>(
    () => product === 'hunts' || getSetting(SHOW_HUNTS_KEY) === 'true',
  )
  const [hasOutreach, setHasOutreach] = useState<boolean>(false)
  const [ready, setReady] = useState<boolean>(product === 'hunts')

  useEffect(() => {
    if (!userId || product !== 'bookme') return
    let cancelled = false
    void (async () => {
      try {
        const [setting, contacts] = await Promise.all([
          supabase
            .from('user_settings')
            .select('setting_value')
            .eq('user_id', userId)
            .eq('setting_key', SHOW_HUNTS_KEY)
            .maybeSingle(),
          supabase
            .from('user_contacts')
            .select('contact_id', { count: 'exact', head: true })
            .eq('user_id', userId),
        ])
        if (cancelled) return
        const value = (setting.data as { setting_value?: string } | null)?.setting_value
        if (value === 'true') setShowHuntsState(true)
        else if (value === 'false') setShowHuntsState(false)
        // No explicit choice yet: switch Hunts on for people who already use it.
        else if ((contacts.count ?? 0) > 0) setShowHuntsState(true)
      } catch {
        // keep the default
      } finally {
        if (!cancelled) setReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, product])

  const huntsVisible = product === 'hunts' || showHunts

  useEffect(() => {
    if (!userId || !huntsVisible) return
    let cancelled = false
    void (async () => {
      try {
        const { count, error } = await supabase
          .from('programs')
          .select('id', { count: 'exact', head: true })
        if (cancelled) return
        setHasOutreach(error ? product === 'hunts' : (count ?? 0) > 0)
      } catch {
        if (!cancelled) setHasOutreach(product === 'hunts')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, huntsVisible, product])

  const setShowHunts = useCallback((next: boolean) => {
    setShowHuntsState(next)
    void setSetting(SHOW_HUNTS_KEY, next ? 'true' : 'false')
  }, [])

  return { showHunts: huntsVisible, hasOutreach, ready, setShowHunts }
}
